import type { Environment } from '../App'

/**
 * File types the SendAPI may have stored for a return label, in the order we
 * prefer them when several exist. Add an entry to look for another type.
 */
export const RETURN_LABEL_EXTENSIONS = ['pdf', 'png', 'jpeg', 'jpg'] as const

const MIME_TYPES: Record<(typeof RETURN_LABEL_EXTENSIONS)[number], string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
}

/**
 * `returnlabels` container of the SendAPI storage account for each hub
 * environment. The real values are SendAPI config (`ReturnLabelStorageOptions`);
 * hardcoded here until the host can supply them. acc shares the tst account.
 */
export const RETURN_LABEL_BASE_URLS: Record<Environment, string> = {
  tst: 'https://sendapitststrg.blob.core.windows.net/returnlabels',
  acc: 'https://sendapitststrg.blob.core.windows.net/returnlabels',
  prd: 'https://sendapiprdstrg.blob.core.windows.net/returnlabels',
}

/** Return orders are `REB_<returnOrderCount + 1>_<orderId>`; only they have a label. */
const RETURN_ORDER_PREFIX = 'REB_'

export interface ReturnLabelFile {
  blob: Blob
  fileName: string
}

export function getReturnLabelFileNames(orderId: string): string[] {
  if (!orderId.startsWith(RETURN_ORDER_PREFIX)) return []
  return RETURN_LABEL_EXTENSIONS.map((extension) => `${orderId}.${extension}`)
}

async function fetchIfExists(
  env: Environment,
  fileName: string,
  signal?: AbortSignal,
): Promise<ReturnLabelFile | null> {
  try {
    const response = await fetch(`${RETURN_LABEL_BASE_URLS[env]}/${fileName}`, {
      signal,
    })
    if (!response.ok) return null
    // Storage may serve the file as `application/octet-stream`, which iOS Safari
    // downloads instead of rendering, so the type is set from the extension.
    const extension = fileName.split('.').pop() as keyof typeof MIME_TYPES
    const blob = await response.blob()
    return { blob: new Blob([blob], { type: MIME_TYPES[extension] }), fileName }
  } catch {
    // A network failure or a cross-origin read the storage account blocks is
    // treated as "no label", which drops the CTA instead of showing a dead link.
    return null
  }
}

/**
 * Looks for every supported file type at once and returns the first that
 * exists, or null. The file is read rather than just probed so the CTA can
 * hand the browser a same-origin blob, the only kind `download` applies to.
 */
export async function fetchReturnLabel(
  orderId: string,
  env: Environment,
  signal?: AbortSignal,
): Promise<ReturnLabelFile | null> {
  const results = await Promise.all(
    getReturnLabelFileNames(orderId).map((name) =>
      fetchIfExists(env, name, signal),
    ),
  )
  return results.find((result) => result !== null) ?? null
}
